import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled, { keyframes } from 'styled-components';

import { fadeIn } from '@sorare/core/src/atoms/animations/keyframes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandXXL, LabelL } from '@sorare/core/src/atoms/typography';
import { TextureBackground } from '@sorare/core/src/atoms/ui/TextureBackground';
import { Story } from '@sorare/core/src/components/Story';
import { usePausableStory } from '@sorare/core/src/components/Story/usePausableStory';
import { Avatar } from '@sorare/core/src/components/user/Avatar';
import { glossary } from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { ChampionshipStoryTitle } from './ChampionshipStoryTitle';
import type { ChampionshipStory_userGroupMembershipShopItemsUpdate } from './__generated__/index.graphql';

const translateY = keyframes`
  from {
    transform: translateY(100px);
  }
  to {
    transform: translateY(0px);
  }
`;

const Root = styled(Vertical)`
  overflow: hidden;
  width: 100%;
  height: 100%;
  padding: var(--double-unit);

  background:
    linear-gradient(to top, var(--c-black), transparent),
    conic-gradient(
        from 90deg at 4px 4px,
        rgba(151, 77, 255, 0.3) 25%,
        var(--c-nl-800) 0
      )
      0 0/5px 5px;

  @media ${laptopAndAbove} {
    min-height: min(calc(90 * var(--unit)), 100vh);
  }
`;
const StyledTextureBackground = styled(TextureBackground)`
  position: absolute;
  inset: 0;
  z-index: -1;
`;
const Content = styled(Vertical).attrs({ gap: 5, center: true })`
  position: relative;
  flex: 1;

  opacity: 0;
  animation:
    ${fadeIn} 0.65s cubic-bezier(0.75, 0.07, 0.23, 0.89) forwards,
    ${translateY} 0.65s ease-in-out forwards;
`;
const AvatarWrapper = styled.div`
  position: relative;
`;
const Blur = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
  filter: blur(39px);
  mix-blend-mode: plus-lighter;

  &.disabled {
    opacity: 0;
  }
`;

type Props = {
  userGroupMembershipShopItemUpdates: ChampionshipStory_userGroupMembershipShopItemsUpdate[];
  onClose: () => void;
};
export const ChampionshipStory = ({
  userGroupMembershipShopItemUpdates,
  onClose,
}: Props) => {
  const { pause, handleOnPause, handleOnUnpause } = usePausableStory();

  return (
    <Story
      pause={pause}
      handleOnPause={handleOnPause}
      handleOnUnpause={handleOnUnpause}
      goBackToBeginning={false}
      onClose={onClose}
      stories={userGroupMembershipShopItemUpdates.map(
        userGroupMembershipShopItemUpdate => {
          const { userGroupMembership, newShopItems } =
            userGroupMembershipShopItemUpdate;

          return {
            duration: 5,
            withBackChevron: false,
            withNextChevron: false,
            autoPlayNext: false,
            bodyElement: ({ onNext }) => (
              <Root>
                <StyledTextureBackground
                  background="wave1"
                  mode="dark"
                  color={['var(--c-black)', 'rgba(var(--c-rgb-black), 0)']}
                  imageOpacity={0.7}
                  withDots
                />
                <Content>
                  <Vertical gap={2} center>
                    <AvatarWrapper>
                      <Avatar
                        user={userGroupMembership.user}
                        size={10}
                        rounded
                      />
                      {newShopItems.length > 0 && (
                        <Blur>
                          <Avatar
                            user={userGroupMembership.user}
                            framePictureUrl={newShopItems.at(0)?.pictureUrl}
                            size={10}
                            rounded
                          />
                        </Blur>
                      )}
                    </AvatarWrapper>
                    <BrandXXL>{userGroupMembership.ranking}</BrandXXL>
                  </Vertical>
                  <Vertical gap={2} center>
                    <ChampionshipStoryTitle
                      userGroupMembershipShopItemUpdate={
                        userGroupMembershipShopItemUpdate
                      }
                    />
                    <LabelL color="var(--c-nd-600)">
                      <FormattedMessage
                        id="ChampionshipStory.subtitle"
                        defaultMessage="Based on your cumulative score in the Championship"
                      />
                    </LabelL>
                  </Vertical>
                </Content>
                <Button
                  color="primary"
                  size="medium"
                  onClick={onNext}
                  fullWidth
                >
                  <FormattedMessage {...glossary.continue} />
                </Button>
              </Root>
            ),
          };
        }
      )}
    />
  );
};

ChampionshipStory.fragments = {
  userGroupMembershipShopItemsUpdate: gql`
    fragment ChampionshipStory_userGroupMembershipShopItemsUpdate on UserGroupMembershipShopItemsUpdate {
      id
      evolution
      newShopItems {
        id
        pictureUrl
      }
      previousShopItems {
        id
        pictureUrl
      }
      userGroupMembership {
        id
        ranking
        user {
          slug
          id
          ...Avatar_user
        }
      }
      ...ChampionshipStoryTitle_userGroupMembershipShopItemsUpdate
    }
    ${ChampionshipStoryTitle.fragments.userGroupMembershipShopItemsUpdate}
    ${Avatar.fragments.user}
  ` as TypedDocumentNode<ChampionshipStory_userGroupMembershipShopItemsUpdate>,
};
